import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Stack,
  TextField, Typography,
} from '@mui/material';
import { VisibilityOutlined, VisibilityOffOutlined } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import authService, { AdminFeatures } from '../services/authService';
import { useAuth } from '../hooks/useAuth';
import { MederiAdminBrand } from '../design-system/MederiAdminBrand';
import '../design-system/design-system.css';
import './LoginPage.css';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 50,
    borderRadius: '9px',
    backgroundColor: '#fff',
    color: 'var(--ds-ink)',
    fontSize: 14,
    fontFamily: 'var(--ds-font)',
    '& fieldset': { borderColor: '#cfcac6' },
    '&:hover fieldset': { borderColor: '#a9a19b' },
    '&.Mui-focused': { boxShadow: '0 0 0 3px #f5dddd' },
    '&.Mui-focused fieldset': { borderColor: '#ad777b', borderWidth: 1 },
  },
  '& .MuiInputBase-input::placeholder': { color: '#aaa39e', opacity: 1 },
};

const submitSx = {
  height: 50,
  borderRadius: '9px',
  bgcolor: 'var(--ds-ink)',
  color: '#fff',
  fontSize: 14,
  fontWeight: 700,
  fontFamily: 'var(--ds-font)',
  textTransform: 'none' as const,
  '&:hover': { bgcolor: '#48443f' },
  '&.Mui-disabled': { bgcolor: '#ddd9d5', color: '#817a74' },
};

const LoginPage: React.FC = () => {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState('');
  const [stage, setStage] = useState<'credentials' | 'code'>('credentials');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [features, setFeatures] = useState<AdminFeatures | null>(null);
  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    authService.getConfig().then(setFeatures).catch(() => setError('ログイン設定を取得できませんでした。画面を再読み込みしてください。'));
  }, []);

  const handleCredentials = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      if (!features) throw new Error('ログイン設定を取得できませんでした。');
      if (features.email_otp_enabled) {
        setChallenge(await authService.startEmail(email.trim().toLowerCase(), password));
        setStage('code');
      } else {
        const result = await authService.loginPassword(email.trim().toLowerCase(), password);
        if (result.user) { completeLogin(result.user); navigate(from, { replace: true }); }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'ログインできませんでした。');
    } finally { setBusy(false); }
  };

  const verifyEmail = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const result = await authService.verifyEmail(challenge, code);
      if (result.user) { completeLogin(result.user); navigate(from, { replace: true }); }
    } catch (err: any) {
      setError(err.response?.data?.message || '確認コードを確認してください。');
    } finally { setBusy(false); }
  };

  const backToCredentials = () => {
    setStage('credentials'); setChallenge(''); setCode(''); setError('');
  };

  return <Box component="main" className="mederi-login admin-page-transition">
    <header className="mederi-login-brand"><MederiAdminBrand /></header>
    <div className="mederi-login-shell">
      <section className="mederi-login-card" aria-labelledby="login-title">
        <div className="mederi-login-card-content">
          <Typography id="login-title" component="h1" className="mederi-login-title">
            {stage === 'credentials' ? '管理画面にログイン' : '確認コードを入力'}
          </Typography>
          {stage === 'code' && <Typography className="mederi-login-lead">{email.trim().toLowerCase()} に送信しました</Typography>}

          {error && <Alert severity="error" className="mederi-login-alert">{error}</Alert>}

          {stage === 'credentials' ? <Box component="form" onSubmit={handleCredentials} className="mederi-login-form">
            <Stack spacing={2.5}>
              <Box>
                <Typography component="label" htmlFor="login-email" className="mederi-login-label">メールアドレス</Typography>
                <TextField id="login-email" type="email" autoComplete="email" placeholder="メールアドレスを入力" fullWidth required autoFocus
                  value={email} onChange={event => setEmail(event.target.value)} sx={fieldSx} />
              </Box>
              <Box>
                <Typography component="label" htmlFor="login-password" className="mederi-login-label">パスワード</Typography>
                <TextField id="login-password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="パスワードを入力" fullWidth required
                  value={password} onChange={event => setPassword(event.target.value)} sx={fieldSx}
                  InputProps={{ endAdornment: <InputAdornment position="end"><IconButton type="button" aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                    onClick={() => setShowPassword(value => !value)} edge="end" size="small">{showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}</IconButton></InputAdornment> }} />
              </Box>
              <Button type="submit" variant="contained" disableElevation disabled={busy || !features || !email.trim() || !password} sx={submitSx}>
                {busy ? <CircularProgress size={21} color="inherit" /> : features?.email_otp_enabled ? '確認コードを送信' : 'ログイン'}
              </Button>
            </Stack>
          </Box> : <Box component="form" onSubmit={verifyEmail} className="mederi-login-form">
            <Stack spacing={2.5}>
              <Box>
                <Typography component="label" htmlFor="login-code" className="mederi-login-label">6桁の確認コード</Typography>
                <TextField id="login-code" placeholder="確認コードを入力" autoComplete="one-time-code" fullWidth required autoFocus
                  inputProps={{ inputMode: 'numeric', maxLength: 6, pattern: '[0-9]{6}' }} value={code} onChange={event => setCode(event.target.value)} sx={fieldSx} />
                <Typography className="mederi-login-hint">有効期限は5分です。</Typography>
              </Box>
              {window.location.hostname === 'localhost' && <Alert severity="info" className="mederi-login-alert">
                ローカル環境では通常の受信箱へは配信されません。<a href="http://localhost:8025" target="_blank" rel="noreferrer">Mailpit</a> で確認してください。
              </Alert>}
              <Button type="submit" variant="contained" disableElevation disabled={busy || !/^\d{6}$/.test(code)} sx={submitSx}>
                {busy ? <CircularProgress size={21} color="inherit" /> : 'ログイン'}
              </Button>
              <Button type="button" onClick={backToCredentials} className="mederi-login-back">入力し直す・コードを再送する</Button>
            </Stack>
          </Box>}
        </div>
      </section>
      <p className="mederi-login-support">ログインできない場合は管理者にお問い合わせください</p>
    </div>
  </Box>;
};

export default LoginPage;
